import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import '../styles/product-card.css';
import { track } from '../utils/analytics';

const fmtCOP = v => new Intl.NumberFormat('es-CO', { style:'currency', currency:'COP', minimumFractionDigits:0 }).format(v || 0);

export default function ProductCard({ product: p }) {
  const { agregar } = useCart();
  const [variaciones, setVariaciones] = useState({});
  const [seleccion, setSeleccion] = useState({});
  const [cargando, setCargando] = useState(false);

  const hasDiscount = Boolean(Number(p.descuento_activo) === 1 && Number(p.descuento_porcentaje) > 0);

  useEffect(() => {
    api.get('/variaciones/' + p.id).then(r => {
      if (Object.keys(r.data.agrupadas).length > 0) {
        setVariaciones(r.data.agrupadas);
        // Seleccionar primera opcion de cada tipo por defecto
        const sel = {};
        for (const tipo of Object.keys(r.data.agrupadas)) {
          sel[tipo] = r.data.agrupadas[tipo][0];
        }
        setSeleccion(sel);
      }
    }).catch(() => {});
  }, [p.id]);

  const tieneVariaciones = Object.keys(variaciones).length > 0;

  // Precio final — si hay variacion con precio propio, ese es el precio total
  // Si precio_adicional < 0 no aplica; si > 0 es precio total de esa variacion
  const precioBase = hasDiscount ? Math.round(p.precio * (1 - p.descuento_porcentaje / 100)) : p.precio;
  const variacionConPrecio = Object.values(seleccion).find(v => v?.precio_adicional > 0);
  const finalPrice = variacionConPrecio ? variacionConPrecio.precio_adicional : precioBase;

  const stars = (rating) => Array.from({ length: 5 }, (_, i) => (
    <span key={i} className={i < Math.round(rating) ? 'star-f' : 'star-e'}>★</span>
  ));

  const handleAgregar = () => {
    // Verificar stock
    if (tieneVariaciones) {
      const tiposFaltantes = Object.keys(variaciones).filter(t => !seleccion[t]);
      if (tiposFaltantes.length > 0) {
        toast.error('Selecciona: ' + tiposFaltantes.join(', '));
        return;
      }
      // Verificar stock de la variacion seleccionada
      const varConStock = Object.values(seleccion).find(v => v.stock !== undefined);
      if (varConStock && varConStock.stock <= 0) {
        toast.error('Sin existencias para esta opcion');
        return;
      }
    } else {
      if (p.stock !== undefined && p.stock <= 0) {
        toast.error('Producto sin existencias');
        return;
      }
    }
    const variacionesSeleccionadas = Object.entries(seleccion).map(([tipo, v]) => ({
      tipo, nombre: v.nombre, valor: v.valor, precio_adicional: v.precio_adicional
    }));
    agregar({ ...p, precio: finalPrice, variaciones: variacionesSeleccionadas });
    track('click_add_carrito', { producto_id: p.id, producto_nombre: p.nombre, valor: finalPrice });
    const varTexto = variacionesSeleccionadas.length > 0
      ? ' (' + variacionesSeleccionadas.map(v => v.valor).join(', ') + ')'
      : '';
    toast.success(p.nombre + varTexto + ' al carrito 🌿');
  };

  const esCombo = p.categoria_id === 'cat-d5331384';
  return (
    <div className={"product-card" + (esCombo ? " combo" : "")}>
      <div className="eco-badge">
        <span>🌱</span> Eco-friendly
      </div>
      {p.stock !== undefined && p.stock <= 0 && !tieneVariaciones && (
        <div className="discount-badge" style={{ background:'#e76f51' }}>Sin stock</div>
      )}
      {hasDiscount && (
        <div className="discount-badge">-{p.descuento_porcentaje}%</div>
      )}
      <Link to={"/producto/" + p.id} className="product-image-link" onClick={() => track('click_ver_detalles', { producto_id: p.id, producto_nombre: p.nombre })}>
        <div className="product-image">
          {p.imagen_principal
            ? <img src={p.imagen_principal} alt={p.nombre} loading="lazy" />
            : <div className="image-placeholder">🌿</div>}
          <div className="image-overlay">
            <span className="view-details">Ver detalles</span>
          </div>
        </div>
      </Link>

      <div className="product-content">
        {p.categoria_nombre ? <div className="product-category">{p.categoria_nombre}</div> : null}
        <Link to={"/producto/" + p.id} className="product-title-link" onClick={() => track('click_ver_detalles', { producto_id: p.id, producto_nombre: p.nombre })}>
          <h3 className="product-title">{p.nombre}</h3>
        </Link>
        {p.descripcion_corta ? <p className="product-description">{p.descripcion_corta}</p> : null}

        {Number(p.total_resenas) > 0 && (
          <div className="product-rating">
            <div className="stars-row">{stars(Number(p.calificacion_promedio) || 0)}</div>
            <span className="reviews-count">({p.total_resenas})</span>
          </div>
        )}

        {/* Variaciones */}
        {tieneVariaciones && (
          <div style={{ marginBottom:10 }}>
            {Object.entries(variaciones).map(([tipo, opciones]) => (
              <div key={tipo} style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--ink-faint)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:5 }}>{tipo}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                  {opciones.map(op => (
                    <button key={op.id} onClick={() => setSeleccion(s => ({...s, [tipo]: op}))}
                      style={{
                        padding:'4px 10px', borderRadius:'var(--r-full)', fontSize:11, fontWeight:600, cursor:'pointer', border:'1.5px solid',
                        borderColor: seleccion[tipo]?.id === op.id ? '#9FBE6A' : 'rgba(28,58,30,0.15)',
                        background: seleccion[tipo]?.id === op.id ? 'rgba(159,190,106,0.15)' : '#fff',
                        color: seleccion[tipo]?.id === op.id ? '#1C3A1E' : 'var(--ink-mid)',
                        transition:'all 0.15s'
                      }}>
                      {op.valor}
                      {op.precio_adicional > 0 && <span style={{ color:'var(--terra)', marginLeft:3 }}>{fmtCOP(op.precio_adicional)}</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="product-price">
          {hasDiscount && !variacionConPrecio
            ? <>
                <span className="original-price">{fmtCOP(p.precio)}</span>
                <span className="final-price discounted">{fmtCOP(finalPrice)}</span>
              </>
            : <span className="final-price">{fmtCOP(finalPrice)}</span>}
        </div>

        <div className="product-actions">
          <button className="btn-add-to-cart" onClick={handleAgregar}
            disabled={!tieneVariaciones && p.stock !== undefined && p.stock <= 0}
            style={{ opacity: !tieneVariaciones && p.stock !== undefined && p.stock <= 0 ? 0.5 : 1, cursor: !tieneVariaciones && p.stock !== undefined && p.stock <= 0 ? 'not-allowed' : 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 4V20M20 12H4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            Agregar
          </button>
          <Link to={"/producto/" + p.id} className="btn-view" onClick={() => track('click_ver_detalles', { producto_id: p.id, producto_nombre: p.nombre })}>Ver</Link>
        </div>
      </div>
    </div>
  );
}
